/*
 * Trigger inherits typography from the host page (the shadow host's inline
 * style allows font to inherit through `all: initial`). Don't hard-code a
 * font-family here — that would override the host site's brand font.
 *
 * Colors come from the host's Scalebloom palette tokens with light-theme
 * fallbacks, aliased once on :host. See dialog.css for the full rationale
 * (and for why lengths are px, not rem).
 *
 * ⚠️ SYNC: dashboard/src/AppearancePreview.svelte renders a static mock
 * of this trigger for the Appearance tab's live preview. Padding, radius,
 * font-size, shadow, the image-mode 64px cap — all duplicated there.
 * When you change any of those here, change them there too.
 */

:host {
  --_base: var(--clr-base, hwb(0 97% 3%));
  --_contrast: var(--clr-contrast, hwb(0 11% 89%));
}

.trigger-floating {
  position: fixed;
  bottom: clamp(12px, 6.29px + 0.952vw, 20px);
  right: clamp(12px, 6.29px + 0.952vw, 20px);
  z-index: 2147483000;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 20px 6px 16px;
  border-radius: 12px;
  /* Trigger has its own bg/fg knobs so customers can recolor it without
     dragging the whole dialog along — falls back to the dialog's
     contrast/base inversion when unset. */
  background: var(--_trigger-bg, var(--_contrast));
  color: var(--_trigger-fg, var(--_base));
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 15px;
  font-weight: 500;
  box-shadow: 0 12px 28px -8px rgba(0, 0, 0, 0.35);
  /* Host-page stylesheets can't reach inside the shadow root, so the color
     flip when the pill scrolls over a dark section (see syncTriggerToSections
     in index.ts) has to fade itself — mirrors the host header's .5s ease. */
  transition:
    transform 0.15s ease-out,
    background-color 0.5s ease,
    color 0.5s ease;

  &:hover {
    transform: scale(1.05);
  }
  & svg {
    width: 18px;
    height: 18px;
  }
}

.trigger-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 8px;
  background: transparent;
  color: var(--_trigger-bg, var(--_contrast));
  border: 1px solid var(--_trigger-bg, var(--_contrast));
  cursor: pointer;
  font: inherit;
  font-size: 15px;

  &:hover {
    background: var(--_trigger-bg, var(--_contrast));
    color: var(--_trigger-fg, var(--_base));
  }
  & svg {
    width: 16px;
    height: 16px;
  }
}

/*
 * Image trigger: composes with .trigger-floating or .trigger-inline so
 * positioning is inherited, but strips the default pill/border chrome so the
 * image is the entire visual, then clips it to a circle.
 *
 * Fixed 64×64 (not max-) because `border-radius: 50%` on a non-square box
 * gives an oval — small uploads must still render in a 64px square. The
 * Cloudinary delivery URL pairs `c_fill,g_auto,w_128,h_128` with this so the
 * source bytes are smart-cropped to a square at 2x retina; `object-fit: cover`
 * here is a belt-and-suspenders for the dashboard preview (which renders the
 * raw upload without the Cloudinary transform).
 */
.trigger-image,
.trigger-image:hover {
  background: transparent;
  border: none;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
  color: inherit;
}
.trigger-image img {
  display: block;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
}
